// Item factory (Phaser-free): turns a def id + rarity into an ItemInstance
// with rolled affixes. Also used by the debug tool (makeItem by id).
//
// Rarity rules
//   standard  — no affixes
//   advanced  — 1 positive identity affix (chain targets, cooling, dash cooldown, shield...)
//   prototype — 1 strong positive affix + 1 negative affix
// Weapon mod slots: standard 1, advanced 2, prototype 2 (+1 with the MOD_SLOT upgrade).

import type { Rng } from '../../core/rng';
import type {
  AffixInstance,
  EquipId,
  EquipInstance,
  EquipSlot,
  ItemInstance,
  ItemKind,
  ModId,
  ModInstance,
  MutationId,
  MutationInstance,
  Rarity,
  StatKey,
  Tag,
  WeaponId,
  WeaponInstance,
} from '../../core/types';
import {
  EQUIPMENT,
  MODS,
  MUTATIONS,
  NEGATIVE_AFFIXES,
  POSITIVE_AFFIXES,
  WEAPONS,
  type AffixDef,
} from '../../data/items';
import { formatStatMod } from './stats';

export const MOD_SLOTS_BY_RARITY: Readonly<Record<Rarity, number>> = {
  standard: 1,
  advanced: 2,
  prototype: 2,
};

export const RARITIES: readonly Rarity[] = ['standard', 'advanced', 'prototype'];

export function rarityRank(r: Rarity): number {
  return r === 'prototype' ? 2 : r === 'advanced' ? 1 : 0;
}

/** Which table a def id belongs to (null for unknown ids). */
export function itemKindOf(defId: string): ItemKind | null {
  if (defId in WEAPONS) return 'weapon';
  if (defId in EQUIPMENT) return 'equipment';
  if (defId in MODS) return 'mod';
  if (defId in MUTATIONS) return 'mutation';
  return null;
}

/** Definition tags of a def id (any kind). */
export function defTags(defId: string): readonly Tag[] {
  switch (itemKindOf(defId)) {
    case 'weapon':
      return WEAPONS[defId as WeaponId].tags;
    case 'equipment':
      return EQUIPMENT[defId as EquipId].tags;
    case 'mod':
      return MODS[defId as ModId].tags;
    case 'mutation':
      return MUTATIONS[defId as MutationId].tags;
    default:
      return [];
  }
}

/** Stats an item's own definition already touches (affixes avoid them to keep identities clean). */
function ownStats(kind: ItemKind, defId: string): Set<StatKey> {
  const out = new Set<StatKey>();
  if (kind === 'equipment') for (const m of EQUIPMENT[defId as EquipId].stats) out.add(m.stat);
  if (kind === 'mutation') for (const m of MUTATIONS[defId as MutationId].stats) out.add(m.stat);
  if (kind === 'mod') {
    const w = MODS[defId as ModId].weapon;
    if (w.damageMult !== undefined) out.add('damage');
    if (w.fireRateMult !== undefined || w.burst) out.add('fireRate');
    if (w.heatMult !== undefined) out.add('heatCapacity');
    if (w.coolMult !== undefined) out.add('cooling');
    if (w.knockbackMult !== undefined) out.add('knockback');
  }
  return out;
}

function affixFits(a: AffixDef, kind: ItemKind, tags: readonly Tag[], slot: EquipSlot | undefined, avoid: Set<StatKey>): boolean {
  if (!a.kinds.includes(kind)) return false;
  if (avoid.has(a.stat)) return false;
  if (a.tags && !a.tags.some((t) => tags.includes(t))) return false;
  if (a.slots && kind === 'equipment' && (!slot || !a.slots.includes(slot))) return false;
  return true;
}

function makeAffix(a: AffixDef, strong: boolean, negative: boolean): AffixInstance {
  const value = strong ? a.strong : a.value;
  const stat = { stat: a.stat, op: a.op, value };
  return {
    id: `${a.id}${strong ? '+' : ''}`,
    label: formatStatMod(stat),
    stats: [stat],
    ...(negative ? { negative: true } : {}),
  };
}

/** Roll the affixes for one item. Deterministic for a given rng state. */
export function rollAffixes(kind: ItemKind, defId: string, rarity: Rarity, rng: Rng, slot?: EquipSlot): AffixInstance[] {
  if (rarity === 'standard') return [];
  const tags = defTags(defId);
  const avoid = ownStats(kind, defId);
  const out: AffixInstance[] = [];
  const strong = rarity === 'prototype';

  const pos = rng.weighted(
    POSITIVE_AFFIXES.filter((a) => affixFits(a, kind, tags, slot, avoid)),
    (a) => a.weight,
  );
  if (pos) {
    out.push(makeAffix(pos, strong, false));
    avoid.add(pos.stat);
  }
  if (strong) {
    const neg = rng.weighted(
      NEGATIVE_AFFIXES.filter((a) => affixFits(a, kind, tags, slot, avoid)),
      (a) => a.weight,
    );
    if (neg) out.push(makeAffix(neg, false, true));
  }
  return out;
}

export function makeWeapon(id: WeaponId, rarity: Rarity, rng: Rng, opts: { bonusModSlots?: number } = {}): WeaponInstance {
  return {
    kind: 'weapon',
    uid: rng.id('wpn_'),
    defId: id,
    rarity,
    affixes: rollAffixes('weapon', id, rarity, rng),
    mods: [],
    modSlots: MOD_SLOTS_BY_RARITY[rarity] + Math.max(0, Math.floor(opts.bonusModSlots ?? 0)),
  };
}

export function makeEquip(id: EquipId, rarity: Rarity, rng: Rng): EquipInstance {
  const slot = EQUIPMENT[id].slot;
  return {
    kind: 'equipment',
    uid: rng.id('eqp_'),
    defId: id,
    slot,
    rarity,
    affixes: rollAffixes('equipment', id, rarity, rng, slot),
  };
}

export function makeMod(id: ModId, rarity: Rarity, rng: Rng): ModInstance {
  return {
    kind: 'mod',
    uid: rng.id('mod_'),
    defId: id,
    rarity,
    affixes: rollAffixes('mod', id, rarity, rng),
  };
}

export function makeMutation(id: MutationId, rarity: Rarity, rng: Rng): MutationInstance {
  return {
    kind: 'mutation',
    uid: rng.id('mut_'),
    defId: id,
    rarity,
    affixes: rollAffixes('mutation', id, rarity, rng),
  };
}

/** Build any item from its def id (kind resolved from the id). Null for unknown ids. */
export function makeItem(defId: string, rarity: Rarity, rng: Rng, opts: { bonusModSlots?: number } = {}): ItemInstance | null {
  const r: Rarity = RARITIES.includes(rarity) ? rarity : 'standard';
  switch (itemKindOf(defId)) {
    case 'weapon':
      return makeWeapon(defId as WeaponId, r, rng, opts);
    case 'equipment':
      return makeEquip(defId as EquipId, r, rng);
    case 'mod':
      return makeMod(defId as ModId, r, rng);
    case 'mutation':
      return makeMutation(defId as MutationId, r, rng);
    default:
      return null;
  }
}
